import React, { Component, Fragment } from 'react';
import classNames from 'classnames';
import { debounce, emptyFn, range, ScrollSpeed } from '../utils';
import { defaultProps } from 'recompose';
import { get } from 'lodash';

import defaultDisplayOptions from '../utils/defaultDisplayOptions';
import defaultLocale from '../utils/defaultLocale';
import defaultTheme from '../utils/defaultTheme';
import Today, { DIRECTION_UP, DIRECTION_DOWN } from '../Today';
import Header from '../Header';
import MonthList from '../MonthList';
import Weekdays from '../Weekdays';
import Years from '../Years';
import Day from '../Day';
import CalendarInfo from '../CalendarInfo';
import Footer from '../Footer';
import RangeInput from '../RangeInput';

import parse from 'date-fns/parse';
import format from 'date-fns/format';
import startOfDay from 'date-fns/start_of_day';


const styles = {
    container: require('./Container.scss'),
    day: require('../Day/Day.scss')
};

export const withDefaultProps = defaultProps({
    autoFocus: true,
    DayComponent: Day,
    display: 'days',
    displayOptions: {},
    HeaderComponent: Header,
    height: 500,
    keyboardSupport: true,
    max: new Date(2050, 11, 31),
    maxDate: new Date(2050, 11, 31),
    min: new Date(1980, 0, 1),
    minDate: new Date(1980, 0, 1),
    onHighlightedDateChange: emptyFn,
    onScroll: emptyFn,
    onScrollEnd: emptyFn,
    onSelect: emptyFn,
    passThrough: {},
    rowHeight: 56,
    tabIndex: 1,
    width: 400,
    YearsComponent: Years,
    RangeInputComponent: RangeInput
});

import { shape, bool, string, func, arrayOf, instanceOf, number, oneOf, oneOfType } from 'prop-types';
import { Divider } from '@material-ui/core';

export default class Calendar extends Component {
    static propTypes = {
        autoFocus: bool,
        className: string,
        DayComponent: func,
        disabledDates: arrayOf(instanceOf(Date)),
        disabledDays: arrayOf(number),
        display: oneOf(['years', 'days']),
        displayOptions: shape({
            hideYearsOnSelect: bool,
            layout: oneOf(['portrait', 'landscape']),
            overscanMonthCount: number,
            shouldHeaderAnimate: bool,
            showHeader: bool,
            showMonthsForYears: bool,
            showOverlay: bool,
            showTodayHelper: bool,
            showWeekdays: bool,
            todayHelperRowOffset: number
        }),
        height: number,
        keyboardSupport: bool,
        locale: shape({
            blank: string,
            headerFormat: string,
            todayLabel: shape({
                long: string,
                short: string
            }),
            weekdays: arrayOf(string),
            weekStartsOn: oneOf([0, 1, 2, 3, 4, 5, 6])
        }),
        max: instanceOf(Date),
        maxDate: instanceOf(Date),
        min: instanceOf(Date),
        minDate: instanceOf(Date),
        onScroll: func,
        onScrollEnd: func,
        onSelect: func,
        rowHeight: number,
        tabIndex: number,
        theme: shape({
            floatingNav: shape({
                background: string,
                chevron: string,
                color: string
            }),
            headerColor: string,
            selectionColor: oneOfType([string, func]),
            textColor: shape({
                active: string,
                default: string
            }),
            todayColor: string,
            weekdayColor: string
        }),
        width: oneOfType([string, number]),
        YearsComponent: func,
        onBoundError: func,
        onChangeBounds: func,        
        setDisableApply: func,
        disableApply: bool,
        useWatchlistsAsAdSource: bool
    };

    constructor(props) {
        super(...arguments);

        this.updateYears(props);

        this.state = {
            display: props.display,
            min: props.min,
            minDate: props.minDate,
            max: props.max,
            maxDate: props.maxDate
        };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const { min, minDate, max, maxDate } = prevState;
        if (nextProps.min !== min || nextProps.minDate !== minDate || nextProps.max !== max || nextProps.maxDate !== maxDate) {
            this && this.updateYears(nextProps);
        }

        return {
            min: nextProps.min,
            minDate: nextProps.minDate,
            max: nextProps.max,
            maxDate: nextProps.maxDate
        };
    }

    componentDidMount() {
        let { autoFocus } = this.props;
        if (autoFocus) {
            this.node.focus();
        }
    }
    _displayOptions = {};
    _locale = {};
    _theme = {};
    getScrollSpeed = new ScrollSpeed().getScrollSpeed;

    updateYears(props = this.props) {
        this._min = parse(props.min);
        this._max = parse(props.max);
        this._minDate = parse(props.minDate);
        this._maxDate = parse(props.maxDate);

        const min = this._min.getFullYear();
        const minMonth = this._min.getMonth();
        const max = this._max.getFullYear();
        const maxMonth = this._max.getMonth();

        const months = [];
        let year, month;
        for (year = min; year <= max; year++) {
            for (month = 0; month < 12; month++) {
                if (
                    year === min && month < minMonth ||
                    year === max && month > maxMonth
                ) {
                    continue;
                }

                months.push({ month, year });
            }
        }

        this.months = months;
    }

    getDisabledDates(disabledDates) {
        return disabledDates && disabledDates.map((date) => format(parse(date), 'YYYY-MM-DD'));
    }

    getDisplayOptions(displayOptions = this.props.displayOptions) {
        return Object.assign(this._displayOptions, defaultDisplayOptions, displayOptions);
    }

    getLocale() {
        return Object.assign(this._locale, defaultLocale, this.props.locale);
    }

    getTheme() {
        return Object.assign(this._theme, defaultTheme, this.props.theme);
    }

    getCurrentOffset = () => {
        return this.scrollTop;
    };

    getDateOffset = (date) => {
        return this._MonthList && this._MonthList.getDateOffset(date);
    };

    scrollTo = (offset) => {
        return this._MonthList && this._MonthList.scrollTo(offset);
    };

    scrollToDate = (date = new Date(), offset, shouldAnimate) => {
        const { display } = this.props;

        return this._MonthList &&
            this._MonthList.scrollToDate(
                date,
                offset,
                shouldAnimate && display === 'days',
                () => this.setState({ isScrolling: false })
            );
    };

    handleScroll = (scrollTop, e) => {
        const { onScroll, rowHeight } = this.props;
        const { isScrolling } = this.state;
        const { showTodayHelper, showOverlay } = this.getDisplayOptions();
        const scrollSpeed = this.scrollSpeed = Math.abs(this.getScrollSpeed(scrollTop));
        this.scrollTop = scrollTop;

        // We only want to display the months overlay if the user is rapidly scrolling
        if (showOverlay && scrollSpeed > rowHeight && !isScrolling) {
            this.setState({
                isScrolling: true
            });
        }

        if (showTodayHelper) {
            this.updateTodayHelperPosition(scrollSpeed);
        }

        onScroll(scrollTop, e);
        this.handleScrollEnd();
    };

    updateTodayHelperPosition = (scrollSpeed) => {
        const today = this.today;
        const scrollTop = this.scrollTop;
        const { showToday } = this.state;
        const { height, rowHeight } = this.props;
        const { todayHelperRowOffset } = this.getDisplayOptions();
        let newState;

        if (!this._todayOffset) {
            this._todayOffset = this.getDateOffset(today);
        }

        // Today is above the fold
        if (scrollTop >= this._todayOffset + (height - rowHeight) / 2 + rowHeight * todayHelperRowOffset) {
            if (showToday !== DIRECTION_UP) {
                newState = DIRECTION_UP;
            }
        }
        // Today is below the fold
        else if (scrollTop <= this._todayOffset - height / 2 - rowHeight * (todayHelperRowOffset + 1)) {
            if (showToday !== DIRECTION_DOWN) {
                newState = DIRECTION_DOWN;
            }
        } else if (showToday && scrollSpeed <= 1) {
            newState = false;
        }

        if (scrollTop === 0) {
            newState = false;
        }

        if (newState !== null) {
            this.setState({ showToday: newState });
        }
    };

    handleScrollEnd = debounce(() => {
        const { onScrollEnd } = this.props;
        const { isScrolling } = this.state;
        const { showTodayHelper } = this.getDisplayOptions();

        if (isScrolling) {
            this.setState({ isScrolling: false });
        }

        if (showTodayHelper) {
            this.updateTodayHelperPosition(0);
        }

        onScrollEnd(this.scrollTop);
    }, 150);

    setDisplay = (display) => {
        this.setState({ display });
    };

    handleBoundError = (isError) => {
        const { onBoundError } = this.props;
        onBoundError && onBoundError(isError);
    }

    handleChangeStartBound = (date) => {
        const { onChangeStartBound } = this.props;
        this.scrollToDate(new Date(date), 0, true);
        onChangeStartBound && onChangeStartBound(date);
    }

    handleChangeEndBound = (date) => {
        const { onChangeEndBound } = this.props;
        this.scrollToDate(new Date(date), 0, true);
        onChangeEndBound && onChangeEndBound(date);
    }

    refNode = (node) => {
        this.node = node;
    };

    refMonth = (monthList) => {
        this._MonthList = monthList;
    };

    refYear = (year) => {
        this._Years = year;
    };

    render() {

        let {
            className,
            passThrough,
            DayComponent,
            disabledDays,
            displayDate,
            height,
            HeaderComponent,
            rowHeight,
            scrollDate,
            selected,
            tabIndex,
            width,
            showTooltip,
            YearsComponent,
            isShowDataCollected,
            calendarData,
            onCancelPicker,
            onApplyPicker,
            adsTypeDate,
            disableApply,
            RangeInputComponent,
            dateRangeLimitInDays,
            useWatchlistsAsAdSource
        } = this.props;
        const connectionData = get(calendarData, 'data', {});
        const connectionIcon = get(calendarData, 'icon');

        const {
            hideYearsOnSelect,
            layout,
            overscanMonthCount,
            shouldHeaderAnimate,
            showHeader,
            showMonthsForYears,
            showOverlay,
            showTodayHelper,
            showWeekdays
        } = this.getDisplayOptions();

        const { display, isScrolling, showToday } = this.state;
        const disabledDates = this.getDisabledDates(this.props.disabledDates);
        const locale = this.getLocale();
        const theme = this.getTheme();
        const today = this.today = startOfDay(new Date());

        
        return (
            <div
                data-test="calendarModal"
                tabIndex={tabIndex}
                className={classNames(className, styles.container.root, {
                    [styles.container.landscape]: layout === "landscape"
                })}
                style={{ color: theme.textColor.default, width }}
                aria-label="Calendar"
                ref={this.refNode}
                {...passThrough.rootNode}
            >
                {showHeader &&
                <HeaderComponent
                    selected={selected}
                    shouldAnimate={Boolean(shouldHeaderAnimate && display !== "years")}
                    layout={layout}
                    theme={theme}
                    locale={locale}
                    scrollToDate={this.scrollToDate}
                    setDisplay={this.setDisplay}
                    dateFormat={locale.headerFormat}
                    display={display}
                    displayDate={displayDate}
                    dataCollected={connectionData}
                    maxDate={this._maxDate}
                    minDate={this._minDate}
                    {...passThrough.Header}
                />
                }
                <RangeInputComponent
                    selected={selected}
                    onChangeStartBound={this.handleChangeStartBound}
                    onChangeEndBound={this.handleChangeEndBound}
                    onError={this.handleBoundError}
                    dateRangeLimitInDays={dateRangeLimitInDays}
                />
                <div className={styles.container.wrapper}>
                    {showWeekdays &&
                    <Weekdays weekdays={locale.weekdays} weekStartsOn={locale.weekStartsOn} theme={theme}/>
                    }
                    <div className={styles.container.listWrapper}>
                        {showTodayHelper &&
                        <Today
                            scrollToDate={this.scrollToDate}
                            show={showToday}
                            today={today}
                            theme={theme}
                            todayLabel={locale.todayLabel.long}
                        />
                        }
                        <MonthList
                            ref={this.refMonth}
                            showTooltip={showTooltip}
                            DayComponent={DayComponent}
                            disabledDates={disabledDates}
                            disabledDays={disabledDays}
                            height={height}
                            isScrolling={isScrolling}
                            locale={locale}
                            maxDate={this._maxDate}
                            min={this._min}
                            minDate={this._minDate}
                            months={this.months}
                            onScroll={this.handleScroll}
                            overscanMonthCount={overscanMonthCount}
                            passThrough={passThrough}
                            theme={theme}
                            today={today}
                            rowHeight={rowHeight}
                            selected={selected}
                            scrollDate={scrollDate}
                            showOverlay={showOverlay}
                            width={width}
                            dataCollected={connectionData}
                            isShowDataCollected={isShowDataCollected}
                            adsTypeDate={adsTypeDate}
                        />
                    </div>
                    {display === "years" &&
                    <YearsComponent
                        ref={this.refYear}
                        dataCollected={calendarData}
                        height={height}
                        hideOnSelect={hideYearsOnSelect}
                        locale={locale}
                        max={this._max}
                        maxDate={this._maxDate}
                        min={this._min}
                        minDate={this._minDate}
                        scrollToDate={this.scrollToDate}
                        selected={selected}
                        setDisplay={this.setDisplay}
                        showMonths={showMonthsForYears}
                        theme={theme}
                        today={today}
                        width={width}
                        years={range(this._min.getFullYear(), this._max.getFullYear() + 1)}
                        {...passThrough.Years}
                    />
                    }
                    <div className={styles.container.divider}>
                        <Divider />
                    </div>
                    {isShowDataCollected && 
                        <Fragment>
                            <CalendarInfo
                                connectionData={connectionData.sourceData || {}}
                                connectionIcon={connectionIcon}
                                useWatchlistsAsAdSource={useWatchlistsAsAdSource}
                            />
                            <div className={styles.container.divider}>
                                <Divider />
                            </div>
                        </Fragment>
                    }
                    <Footer
                        onApply={onApplyPicker}
                        disableApply={disableApply}
                        onCancel={onCancelPicker}
                    />
                </div>
            </div>
        );
    }
};
